import { z } from 'zod';

/*
 * dijitalapp.com appearance, chosen in the admin panel: one of three ready-made themes, and on top of
 * it colours, heading font, corners, the hero layout and the order of the sections. Language-neutral
 * (one document for Turkish and English), versioned like the content.
 */

export const SITE_PRESETS = ['clean', 'warm', 'bold'] as const;
export type SitePreset = (typeof SITE_PRESETS)[number];
export const SITE_HEADING_FONTS = ['preset', 'serif', 'sans', 'heavy'] as const;
export const SITE_RADII = ['preset', 'sharp', 'soft', 'round'] as const;
export const SITE_HERO_VISUALS = ['preset', 'phone', 'desktop'] as const;
export const SITE_HERO_ALIGNS = ['preset', 'split', 'center'] as const;
/** Sections whose order can be changed (the hero stays first, the closing band and footer last). */
export const SITE_SECTIONS = ['product', 'security', 'business', 'plus', 'plans', 'apps', 'faq'] as const;
export type SiteSection = (typeof SITE_SECTIONS)[number];

/** What each theme looks like when nothing is overridden. */
export const SITE_PRESET_DEFAULTS: Record<
  SitePreset,
  { accent: string; secondary: string; background: string; text: string; headingFont: 'serif' | 'sans' | 'heavy'; radius: 'sharp' | 'soft' | 'round'; heroVisual: 'phone' | 'desktop'; heroAlign: 'split' | 'center' }
> = {
  clean: { accent: '#2563eb', secondary: '#0f8a5f', background: '#ffffff', text: '#0a2540', headingFont: 'sans', radius: 'soft', heroVisual: 'desktop', heroAlign: 'split' },
  warm: { accent: '#d9542a', secondary: '#2f7d5b', background: '#fff9f1', text: '#2a1f17', headingFont: 'serif', radius: 'round', heroVisual: 'phone', heroAlign: 'split' },
  bold: { accent: '#3b5bff', secondary: '#c8f53c', background: '#f3f3ee', text: '#0d0d0d', headingFont: 'heavy', radius: 'sharp', heroVisual: 'desktop', heroAlign: 'center' },
};

/** '' means "the theme's own colour". */
const colour = z.union([z.literal(''), z.string().regex(/^#[0-9a-fA-F]{6}$/)]);

export const siteAppearanceSchema = z.object({
  preset: z.enum(SITE_PRESETS),
  colors: z.object({ accent: colour, secondary: colour, background: colour, text: colour }),
  headingFont: z.enum(SITE_HEADING_FONTS),
  radius: z.enum(SITE_RADII),
  heroVisual: z.enum(SITE_HERO_VISUALS),
  heroAlign: z.enum(SITE_HERO_ALIGNS),
  sectionOrder: z
    .array(z.enum(SITE_SECTIONS))
    .length(SITE_SECTIONS.length)
    .refine((order) => new Set(order).size === SITE_SECTIONS.length, 'each section exactly once'),
});
export type SiteAppearance = z.infer<typeof siteAppearanceSchema>;

export const SITE_APPEARANCE_DEFAULT: SiteAppearance = {
  preset: 'warm',
  colors: { accent: '', secondary: '', background: '', text: '' },
  headingFont: 'preset',
  radius: 'preset',
  heroVisual: 'preset',
  heroAlign: 'preset',
  sectionOrder: [...SITE_SECTIONS],
};

/** The choices with "preset" resolved to the theme's own values. */
export function resolveAppearance(a: SiteAppearance) {
  const d = SITE_PRESET_DEFAULTS[a.preset];
  return {
    preset: a.preset,
    colors: {
      accent: a.colors.accent || d.accent,
      secondary: a.colors.secondary || d.secondary,
      background: a.colors.background || d.background,
      text: a.colors.text || d.text,
    },
    custom: { accent: !!a.colors.accent, secondary: !!a.colors.secondary, background: !!a.colors.background, text: !!a.colors.text },
    headingFont: a.headingFont === 'preset' ? d.headingFont : a.headingFont,
    radius: a.radius === 'preset' ? d.radius : a.radius,
    heroVisual: a.heroVisual === 'preset' ? d.heroVisual : a.heroVisual,
    heroAlign: a.heroAlign === 'preset' ? d.heroAlign : a.heroAlign,
    sectionOrder: a.sectionOrder,
  };
}

export interface AdminSiteAppearanceDto {
  appearance: SiteAppearance;
  defaults: SiteAppearance;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  history: { id: string; version: number; createdAt: string; createdBy: string | null }[];
}
